import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';
import { defer, Observable, of } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs';

/**
 * Interfaz genérica para la instancia de reCAPTCHA expuesta por el SDK de Google (Enterprise o Standard).
 */
interface RecaptchaInstance {
  ready?: (cb: () => void) => void;
  execute?: (siteKey: string, options: { action: string }) => Promise<string>;
}

/**
 * Extensión de Window para tipar la variable global `grecaptcha` de manera estricta sin recurrir a `any`.
 */
interface WindowWithRecaptcha extends Window {
  grecaptcha?: RecaptchaInstance & {
    enterprise?: RecaptchaInstance;
  };
}

const SCRIPT_ID_ENTERPRISE = 'recaptcha-enterprise-script';
const SCRIPT_ID_STANDARD = 'recaptcha-standard-script';

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly document = inject(DOCUMENT);
  // Acceso seguro al objeto Window desde DOCUMENT (previene referencias globales directas)
  private readonly win = this.document.defaultView as WindowWithRecaptcha | null;
  private readonly siteKey = environment.recaptcha.ID;

  /**
   * Pipeline RxJS para la carga diferida del script Enterprise.
   * - `defer`: Evalúa la creación del observable en el momento de la primera suscripción.
   * - `shareReplay(1)`: Cachea el resultado de la carga del script para suscripciones posteriores.
   */
  private readonly enterpriseApi$ = defer(() =>
    this.loadScript(
      SCRIPT_ID_ENTERPRISE,
      `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`,
      () => !!this.win?.grecaptcha?.enterprise,
    ),
  ).pipe(
    shareReplay(1),
    catchError((err) => {
      console.warn('Enterprise reCAPTCHA failed, falling back to standard v3:', err);
      return of(undefined);
    }),
  );

  /**
   * Pipeline RxJS de respaldo (fallback) para reCAPTCHA Standard v3.
   * También utiliza `shareReplay(1)` para evitar re-inyectar o re-evaluar el script en ejecuciones subsecuentes.
   */
  private readonly standardApi$ = defer(() =>
    this.loadScript(
      SCRIPT_ID_STANDARD,
      `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`,
      () => !!(this.win?.grecaptcha?.ready || this.win?.grecaptcha?.execute),
    ),
  ).pipe(shareReplay(1));

  /**
   * Solicita y genera un token de validación de reCAPTCHA para la acción especificada.
   * Contempla bypass por entornos de prueba y maneja fallback a Standard v3 si Enterprise no está disponible.
   */
  execute(action: string): Observable<string | undefined> {
    if (environment.recaptcha.skipVerification) {
      return of('skip-recaptcha');
    }
    if (!environment.recaptcha.enabled) {
      return of(undefined);
    }

    return this.enterpriseApi$.pipe(
      switchMap(() => {
        const enterprise = this.win?.grecaptcha?.enterprise;
        if (enterprise) {
          return this.runExecute(enterprise, action);
        }
        // Fallback dinámico a Standard v3 si el script Enterprise no logró exponer el objeto enterprise
        return this.standardApi$.pipe(
          switchMap(() => this.runExecute(this.win?.grecaptcha, action)),
        );
      }),
    );
  }

  /**
   * Método auxiliar genérico para inyectar scripts al DOM.
   * Unifica la creación del tag `<script>`, la asignación de atributos y la gestión de eventos (`onload` / `onerror`).
   */
  private loadScript(id: string, src: string, checkReady: () => boolean): Observable<void> {
    if (this.document.getElementById(id) || checkReady()) {
      return of(undefined);
    }

    return new Observable<void>((subscriber) => {
      const script = this.document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        const check = () => {
          if (checkReady()) {
            subscriber.next(undefined);
            subscriber.complete();
          } else {
            subscriber.error(new Error(`El script ${id} cargó pero la API de reCAPTCHA no está disponible`));
          }
        };

        // Utiliza el callback nativo `grecaptcha.ready()` provisto por el SDK en lugar de bucles de retardo imperativos
        const g = this.win?.grecaptcha;
        if (typeof g?.ready === 'function') {
          g.ready(check);
        } else {
          check();
        }
      };

      script.onerror = () => subscriber.error(new Error(`Falló la carga del script de reCAPTCHA (${id})`));
      this.document.head.appendChild(script);
    });
  }

  /**
   * Invoca la función `.execute(...)` de la API de reCAPTCHA de manera unificada.
   */
  private runExecute(api: RecaptchaInstance | undefined, action: string): Observable<string> {
    return new Observable<string>((subscriber) => {
      if (!api || typeof api.execute !== 'function') {
        subscriber.error(new Error('API de reCAPTCHA no disponible para ejecución'));
        return;
      }

      const executeFn = () => {
        api
          .execute!(this.siteKey, { action })
          .then((token) => {
            subscriber.next(token);
            subscriber.complete();
          })
          .catch((err: unknown) => subscriber.error(err));
      };

      if (typeof api.ready === 'function') {
        api.ready(executeFn);
      } else {
        executeFn();
      }
    });
  }
}